import React, { ReactElement, useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classnames from 'classnames'
import { DocumentNode } from 'graphql'

import { ExportContext } from '../../contexts/ExportContext'
import { ReportStatus } from '../../typings/report'

import styles from './styles.module.css'

type PartialReportStatus = Pick<ReportStatus, 'hasError' | 'loading'>

interface Props<VariablesT> {
  fetchReport?(): void
  fetchExport?(): void
  reportStatus?: PartialReportStatus
  exportStatus?: PartialReportStatus
  disabled?: boolean
  className?: string
  subscriptionDocument?: DocumentNode
  subscriptionReportName?: string
  subscriptionVariables?: VariablesT
}

const ReportActions = <VariablesT extends {}>({
  fetchReport,
  fetchExport,
  reportStatus,
  exportStatus,
  disabled,
  className,
  subscriptionDocument,
  subscriptionReportName,
  subscriptionVariables,
}: Props<VariablesT>): ReactElement => {
  const { runExport, exportState } = useContext(ExportContext)

  const subscriptionExportButtonContent = () => {
    switch (true) {
      case exportState.running:
        return (
          <>
            <FontAwesomeIcon
              icon={['fas', 'spinner']}
              pulse
              className={styles.icon}
            />
            Exporting Report
          </>
        )
      default:
        return (
          <>
            <FontAwesomeIcon
              icon={['fas', 'file-csv']}
              className={styles.icon}
            />
            Export Report
          </>
        )
    }
  }
  const exportButtonContent = () => {
    switch (true) {
      case exportStatus.loading:
        return (
          <>
            <FontAwesomeIcon
              icon={['fas', 'spinner']}
              pulse
              className={styles.icon}
            />
            Exporting Report
          </>
        )
      case exportStatus.hasError:
        return (
          <>
            <FontAwesomeIcon
              icon={['fas', 'exclamation-circle']}
              className={styles.icon}
            />
            Unable to Export
          </>
        )
      default:
        return (
          <>
            <FontAwesomeIcon
              icon={['fas', 'file-csv']}
              className={styles.icon}
            />
            Export Report
          </>
        )
    }
  }
  const reportButtonContent = () => {
    switch (true) {
      case reportStatus.loading:
        return (
          <>
            <FontAwesomeIcon
              icon={['fas', 'spinner']}
              pulse
              className={styles.icon}
            />
            Retrieving Report
          </>
        )
      case reportStatus.hasError:
        return (
          <>
            <FontAwesomeIcon
              icon={['fas', 'exclamation-circle']}
              className={styles.icon}
            />
            Unable to Run Report
          </>
        )
      default:
        return 'Run Report'
    }
  }

  return (
    <div className={classnames(styles.actions, className)}>
      {subscriptionDocument && subscriptionReportName && (
        <button
          onClick={() =>
            runExport(
              subscriptionReportName,
              subscriptionDocument,
              subscriptionVariables
            )
          }
          className={styles.export}
          disabled={exportState.running}
        >
          {subscriptionExportButtonContent()}
        </button>
      )}
      {fetchExport && (
        <button
          onClick={fetchExport}
          className={classnames(styles.export, {
            [styles.fail]: exportStatus.hasError,
          })}
          disabled={exportStatus.loading || disabled}
        >
          {exportButtonContent()}
        </button>
      )}
      {fetchReport && (
        <button
          className={classnames(styles.report, {
            [styles.fail]: reportStatus.hasError,
          })}
          onClick={fetchReport}
          disabled={reportStatus.loading}
        >
          {reportButtonContent()}
        </button>
      )}
    </div>
  )
}

export default ReportActions
